import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Pagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { FileAttachment } from "../types";
import { isAgentProxyUrl } from "../../../../services/process/api";
import { getLoggedinUser } from "../../../../api/apiCore";
import { downloadFile } from "../../../../services/process/downloadFile";
import { formatFileSize } from "../../../../utils";
import { agentApi } from "../../../../api";
import logger from "../../../../utils/logger";

interface Props {
  attachment: FileAttachment;
  isOpen: boolean;
  onClose: () => void;
  isDarkTheme?: boolean;
}

const AdvancedDocumentPreview: React.FC<Props> = ({
  attachment,
  isOpen,
  onClose,
  isDarkTheme = true,
}) => {
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const [textContent, setTextContent] = useState<string>("");
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState<string>("");
  const [imageUrl, setImageUrl] = useState<string>("");

  // Estados para funcionalidades CSV dinâmicas
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortColumn, setSortColumn] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(50);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Configurações de cores baseadas no tema
  const themeColors = {
    // Cores para modo escuro (atual)
    dark: {
      modalBg: "#2b2b2b",
      headerBg: "#1a1a1a",
      toolbarBg: "#333333",
      contentBg: "#424242",
      textContentBg: "#ffffff", // Fundo branco para o conteúdo de texto
      textPrimary: "#ffffff",
      textSecondary: "rgba(255, 255, 255, 0.7)",
      textContentColor: "#000000", // Texto preto no fundo branco
      iconColor: "#ffffff",
      buttonBg: "rgba(255, 255, 255, 0.1)",
      buttonHoverBg: "rgba(255, 255, 255, 0.2)",
      borderColor: "#404040",
      loadingBg: "rgba(0, 0, 0, 0.8)",
      errorBg: "rgba(139, 0, 0, 0.9)",
    },
    // Cores para modo claro (novo)
    light: {
      modalBg: "#ffffff",
      headerBg: "#ffffff",
      toolbarBg: "#e9ecef",
      contentBg: "#f5f5f5",
      textContentBg: "#ffffff", // Fundo branco para o conteúdo de texto
      textPrimary: "#000000",
      textSecondary: "rgba(0, 0, 0, 0.7)",
      textContentColor: "#000000", // Texto preto no fundo branco
      iconColor: "#000000",
      buttonBg: "rgba(0, 0, 0, 0.05)",
      buttonHoverBg: "rgba(0, 0, 0, 0.1)",
      borderColor: "#dee2e6",
      loadingBg: "rgba(255, 255, 255, 0.9)",
      errorBg: "rgba(220, 53, 69, 0.9)",
    },
  };

  const colors = isDarkTheme ? themeColors.dark : themeColors.light;

  const isImageFile = (type: string, name: string): boolean => {
    if (type.startsWith("image/")) return true;
    const ext = name.toLowerCase();
    return [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp"].some(
      (e) => ext.endsWith(e),
    );
  };

  // Função para obter URL autenticada para agent-proxy
  const getAuthenticatedUrl = useCallback(
    async (url: string): Promise<string> => {
      if (!isAgentProxyUrl(url)) {
        return url; // Se não é agent-proxy, retorna a URL original
      }

      try {
        setIsLoadingDocument(true);
        setDocumentError("");

        // Usar a mesma lógica do downloadFileFromAgentProxy
        const parsedURL = new URL(url);
        const queryParams = parsedURL.searchParams;

        const fileURL = queryParams.get("url");
        const timestamp = queryParams.get("t");
        const token = queryParams.get("token");

        // Construir URL do endpoint correto
        const baseUrl =
          process.env.REACT_APP_PROCESS_API_BASE_URL ||
          "https://dev-agent-proxy.jesss.ai";
        const endpoint = `${baseUrl}/api/workflow/download`;
        const params = new URLSearchParams();

        fileURL && params.append("url", fileURL);
        timestamp && params.append("t", timestamp);
        token && params.append("token", token);

        const fullURL = `${endpoint}?${params.toString()}`;

        // Obter informações de autenticação
        const apiKey = process.env.REACT_APP_PROCESS_API_KEY;
        const user = getLoggedinUser();
        const companyId = user?.companyID || user?.companyId;

        // Verificar se é arquivo de texto (incluindo CSV)
        const isTextFile =
          attachment.type.startsWith("text/") ||
          attachment.name.toLowerCase().endsWith(".csv") ||
          attachment.type.includes("csv");

        const response = await agentApi.get(fullURL, {
          responseType: isTextFile ? "text" : "blob",
          withCredentials: true,
          headers: {
            accept: isTextFile ? "text/plain" : "application/octet-stream",
            ...(companyId && { "X-Company-ID": companyId }),
            ...(apiKey && { Authorization: apiKey }), // Sem "Bearer "
          },
        });

        if (response.status !== 200) {
          throw new Error(`Failed to download file: ${response.status}`);
        }

        // Para arquivos de texto (incluindo CSV), retornar o conteúdo diretamente
        if (isTextFile) {
          setTextContent(response.data);
          return ""; // Não precisamos de URL para texto
        }

        // Para PDFs ou imagens, criar URL blob local
        const blob = response.data;

        // Garantir que o content-type seja correto
        let contentType = response.headers["content-type"] || attachment.type;
        if (
          attachment.type === "application/pdf" &&
          !contentType.includes("pdf")
        ) {
          contentType = "application/pdf";
        }

        // Criar novo blob com content-type correto se necessário
        const correctedBlob = new Blob([blob], { type: contentType });
        const blobUrl = URL.createObjectURL(correctedBlob);

        return blobUrl;
      } catch (error) {
        logger.error("Erro ao obter URL autenticada:", error);
        throw error; // Propagar erro para o useEffect tratar
      }
    },
    [attachment.type, attachment.name],
  );

  // Função para carregar conteúdo de texto de URLs não-agent-proxy
  const loadTextContent = useCallback(async (url: string): Promise<void> => {
    try {
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) {
        throw new Error(`Failed to fetch text content: ${response.status}`);
      }
      const text = await response.text();
      setTextContent(text);
    } catch (error) {
      logger.error("Erro ao carregar conteúdo de texto:", error);
      throw error;
    }
  }, []);

  // Carregar URL do documento quando o modal abre
  useEffect(() => {
    if (isOpen && attachment.url) {
      const loadDocument = async () => {
        try {
          setIsLoadingDocument(true);
          setDocumentError("");
          setDocumentUrl("");
          setImageUrl("");
          setTextContent("");

          // Verificar se é arquivo de texto (incluindo CSV)
          const isTextFile =
            attachment.type.startsWith("text/") ||
            attachment.name.toLowerCase().endsWith(".csv") ||
            attachment.type.includes("csv");

          // Verificar se é imagem
          const isImage = isImageFile(attachment.type, attachment.name);

          if (isTextFile && attachment.url) {
            if (isAgentProxyUrl(attachment.url)) {
              await getAuthenticatedUrl(attachment.url);
            } else {
              await loadTextContent(attachment.url);
            }
          } else if (isImage && attachment.url) {
            const url = await getAuthenticatedUrl(attachment.url);
            setImageUrl(url);
          } else {
            const url = await getAuthenticatedUrl(attachment.url || "");
            setDocumentUrl(url);
          }
        } catch (error: unknown) {
          logger.error("Erro ao carregar documento:", error);
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          setDocumentError(`Erro ao carregar documento: ${errorMessage}`);
        } finally {
          setIsLoadingDocument(false);
        }
      };

      loadDocument();
    }
  }, [
    isOpen,
    attachment.name,
    attachment.url,
    attachment.type,
    getAuthenticatedUrl,
    loadTextContent,
  ]);

  // Cleanup: revogar URL blob quando fechar modal ou mudar URLs
  useEffect(() => {
    return () => {
      if (documentUrl && documentUrl.startsWith("blob:")) {
        URL.revokeObjectURL(documentUrl);
      }
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [documentUrl, imageUrl]);

  const handleDownload = async () => {
    if (!attachment.url) return;

    try {
      await downloadFile(attachment.url, attachment.name);
    } catch (error) {
      logger.error("Erro ao fazer download:", error);
      // Fallback para método simples se a função downloadFile falhar
      const link = document.createElement("a");
      link.href = attachment.url;
      link.download = attachment.name;
      link.click();
    }
  };

  // Processar dados CSV com funcionalidades avançadas
  const csvData = useMemo(() => {
    if (!textContent) return { headers: [], rows: [], totalRows: 0 };

    const lines = textContent.split("\n").filter((line) => line.trim());
    if (lines.length === 0) return { headers: [], rows: [], totalRows: 0 };

    const headers = lines[0]
      .split(",")
      .map((cell) => cell.trim().replace(/^"|"$/g, ""));
    const allRows = lines.slice(1).map((line, index) => ({
      id: index,
      cells: line.split(",").map((cell) => cell.trim().replace(/^"|"$/g, "")),
    }));

    // Filtrar por termo de busca
    let filteredRows = allRows;
    if (searchTerm) {
      filteredRows = allRows.filter((row) =>
        row.cells.some((cell) =>
          cell.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    }

    // Ordenar se necessário
    if (sortColumn !== null) {
      filteredRows.sort((a, b) => {
        const aVal = a.cells[sortColumn] || "";
        const bVal = b.cells[sortColumn] || "";

        // Tentar comparação numérica primeiro
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
        }

        // Comparação de string
        const comparison = aVal.localeCompare(bVal);
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    return {
      headers,
      rows: filteredRows,
      totalRows: filteredRows.length,
    };
  }, [textContent, searchTerm, sortColumn, sortDirection]);

  // Dados paginados
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return csvData.rows.slice(startIndex, endIndex);
  }, [csvData.rows, currentPage, rowsPerPage]);

  // Funções de controle
  const handleSort = (columnIndex: number) => {
    if (sortColumn === columnIndex) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(columnIndex);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const handleZoom = (direction: "in" | "out") => {
    setZoomLevel((prev) => {
      const newLevel = direction === "in" ? prev + 10 : prev - 10;
      return Math.max(60, Math.min(200, newLevel));
    });
  };

  const handleRowSelect = (rowId: number) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const totalPages = Math.ceil(csvData.totalRows / rowsPerPage);

  const canPreview =
    attachment.type === "application/pdf" ||
    attachment.type.startsWith("text/") ||
    isImageFile(attachment.type, attachment.name);

  if (!canPreview) {
    return (
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            bgcolor: colors.modalBg,
            color: colors.textPrimary,
          },
        }}
      >
        <Box sx={{ m: 0, p: 2, textAlign: "center", position: "relative" }}>
          <Typography
            variant="h6"
            component="div"
            sx={{ color: colors.textPrimary }}
          >
            Pré-visualização não disponível
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#ffffff", // Ícone sempre branco
              bgcolor: "rgba(0, 0, 0, 0.2)", // Fundo translúcido
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.3)", // Fundo mais escuro no hover
              },
              width: 40,
              height: 40,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ textAlign: "center", py: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "12px",
                bgcolor: isDarkTheme
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
                fontSize: "32px",
              }}
            >
              📄
            </Box>
            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
              {attachment.name}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              {formatFileSize(attachment.size)}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "8px",
              textTransform: "none",
              px: 3,
              py: 1.5,
            }}
          >
            Baixar Arquivo
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          bgcolor: colors.modalBg,
          backgroundColor: colors.modalBg,
          background: colors.modalBg,
          color: colors.textPrimary,
          width: "90vw",
          height: "85vh",
          maxWidth: "1200px",
          maxHeight: "800px",
          m: 2,
          p: 0, // Remove padding interno do modal
          "& .MuiDialogTitle-root": {
            backgroundColor: colors.headerBg + " !important",
            background: colors.headerBg + " !important",
          },
          "& .MuiDialogContent-root": {
            padding: 0,
            margin: 0,
            border: "none",
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: colors.headerBg,
          borderBottom: `1px solid ${colors.borderColor}`,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: colors.headerBg,
          backgroundColor: colors.headerBg,
        }}
        style={{
          backgroundColor: colors.headerBg,
          background: colors.headerBg,
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "#ffffff", // Ícone sempre branco
            bgcolor: "rgba(0, 0, 0, 0.2)", // Fundo translúcido
            "&:hover": {
              bgcolor: "rgba(0, 0, 0, 0.3)", // Fundo mais escuro no hover
            },
            width: 40,
            height: 40,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ textAlign: "center", flex: 1, mx: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: "#ffffff", fontWeight: 600 }}
          >
            {attachment.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.8)" }}
          >
            {formatFileSize(attachment.size)}
          </Typography>
        </Box>

        <IconButton
          aria-label="download"
          onClick={handleDownload}
          sx={{
            color: "#ffffff", // Ícone sempre branco
            bgcolor: "rgba(0, 0, 0, 0.2)", // Fundo translúcido
            "&:hover": {
              bgcolor: "rgba(0, 0, 0, 0.3)", // Fundo mais escuro no hover
            },
            width: 40,
            height: 40,
          }}
        >
          <DownloadIcon />
        </IconButton>
      </Box>

      {/* Conteúdo do documento */}
      <DialogContent
        dividers
        sx={{
          p: 0, // Remove todo padding
          m: 0, // Remove todo margin
          bgcolor: isDarkTheme ? "#2b2b2b" : "#f5f5f5",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "stretch",
          height: "100%",
          border: "none",
          borderRadius: 0,
          boxShadow: "none",
        }}
        style={{
          backgroundColor: isDarkTheme ? "#2b2b2b" : "#f5f5f5",
          padding: 0,
          margin: 0,
          border: "none",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "relative",
            p: 0,
            m: 0,
            border: "none",
            boxShadow: "none",
            ...(textContent && {
              bgcolor: "transparent",
              background: "transparent",
            }),
          }}
          style={{
            padding: 0,
            margin: 0,
            border: "none",
            boxShadow: "none",
            ...(textContent && {
              backgroundColor: "transparent",
              background: "transparent",
            }),
          }}
        >
          {isLoadingDocument && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                zIndex: 10,
                bgcolor: colors.loadingBg,
                padding: 3,
                borderRadius: 2,
                color: colors.textPrimary,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  border: "3px solid #333",
                  borderTop: "3px solid #fff",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Typography>Carregando documento...</Typography>
            </Box>
          )}

          {documentError && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                zIndex: 10,
                bgcolor: colors.errorBg,
                padding: 3,
                borderRadius: 2,
                color: "white",
                textAlign: "center",
              }}
            >
              <i
                className="fas fa-exclamation-triangle"
                style={{ fontSize: 32 }}
              />
              <Typography variant="h6">Erro de Autenticação</Typography>
              <Typography variant="body2">{documentError}</Typography>
              <Button
                variant="contained"
                onClick={() => window.location.reload()}
                sx={{ mt: 1 }}
              >
                Recarregar Página
              </Button>
            </Box>
          )}

          {/* Renderização para arquivos de texto */}
          {textContent && !isLoadingDocument && !documentError && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: "#ffffff !important",
                background: "#ffffff !important",
                backgroundColor: "#ffffff !important",
                overflow: "auto",
                p: 0,
                m: 0,
                border: "none",
                borderRadius: 0,
                boxShadow: "none",
              }}
              style={{
                backgroundColor: "#ffffff !important",
                background: "#ffffff !important",
                padding: 0,
                margin: 0,
                border: "none",
                boxShadow: "none",
                borderRadius: 0,
              }}
            >
              {/* Renderização especial para CSV ou texto normal */}
              {attachment.name.toLowerCase().endsWith(".csv") ||
              attachment.type.includes("csv") ? (
                /* Visualização CSV Dinâmica e Responsiva */
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    bgcolor: "#ffffff !important",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Barra de ferramentas superior */}
                  <Box
                    sx={{
                      bgcolor: "#f8f9fa",
                      borderBottom: "2px solid #dee2e6",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    {/* Informações e busca */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: "#17a2b8",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                        }}
                      >
                        <i
                          className="fas fa-table"
                          style={{ fontSize: "14px" }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: "#2c3e50" }}
                        >
                          Planilha CSV Interativa
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          <Chip
                            label={`${csvData.totalRows} linhas`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`${csvData.headers.length} colunas`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                          {selectedRows.size > 0 && (
                            <Chip
                              label={`${selectedRows.size} selecionadas`}
                              size="small"
                              color="success"
                              variant="filled"
                            />
                          )}
                        </Box>
                      </Box>

                      {/* Campo de busca */}
                      <TextField
                        size="small"
                        placeholder="Buscar em todas as colunas..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 250 }}
                      />
                    </Box>

                    {/* Controles de zoom e ações */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Tooltip title="Diminuir zoom">
                        <IconButton
                          onClick={() => handleZoom("out")}
                          size="small"
                        >
                          <ZoomOutIcon />
                        </IconButton>
                      </Tooltip>
                      <Typography
                        variant="caption"
                        sx={{ minWidth: 40, textAlign: "center" }}
                      >
                        {zoomLevel}%
                      </Typography>
                      <Tooltip title="Aumentar zoom">
                        <IconButton
                          onClick={() => handleZoom("in")}
                          size="small"
                        >
                          <ZoomInIcon />
                        </IconButton>
                      </Tooltip>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6c757d",
                          fontFamily: "monospace",
                          ml: 2,
                        }}
                      >
                        {formatFileSize(attachment.size)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Container da tabela com scroll e zoom */}
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "auto",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: "fit-content",
                        transform: `scale(${zoomLevel / 100})`,
                        transformOrigin: "top left",
                        border: "1px solid #e9ecef",
                      }}
                    >
                      {/* Header fixo */}
                      <Box
                        sx={{
                          display: "flex",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          bgcolor: "#343a40",
                          borderBottom: "2px solid #495057",
                        }}
                      >
                        {/* Coluna seletor */}
                        <Box
                          sx={{
                            minWidth: "50px",
                            maxWidth: "50px",
                            bgcolor: "#495057",
                            borderRight: "1px solid #6c757d",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 1.5,
                            position: "sticky",
                            left: 0,
                            zIndex: 11,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#ffffff",
                              fontWeight: 600,
                            }}
                          >
                            #
                          </Typography>
                        </Box>

                        {/* Headers das colunas */}
                        {csvData.headers.map((header, index) => (
                          <Box
                            key={index}
                            onClick={() => handleSort(index)}
                            sx={{
                              minWidth: "150px",
                              maxWidth: "300px",
                              borderRight:
                                index < csvData.headers.length - 1
                                  ? "1px solid #6c757d"
                                  : "none",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: 2,
                              py: 1.5,
                              cursor: "pointer",
                              "&:hover": {
                                bgcolor: "#495057",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#ffffff",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                              }}
                            >
                              {header || `Coluna ${index + 1}`}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                ml: 1,
                              }}
                            >
                              {sortColumn === index && (
                                <SwapVertIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "#ffffff",
                                    transform:
                                      sortDirection === "desc"
                                        ? "rotate(180deg)"
                                        : "none",
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>

                      {/* Linhas de dados */}
                      {paginatedData.map((row, index) => {
                        const isSelected = selectedRows.has(row.id);
                        const isEvenRow = index % 2 === 0;
                        const actualRowNumber =
                          (currentPage - 1) * rowsPerPage + index + 1;

                        return (
                          <Box
                            key={row.id}
                            sx={{
                              display: "flex",
                              borderBottom: "1px solid #e9ecef",
                              bgcolor: isSelected
                                ? "#e3f2fd"
                                : isEvenRow
                                  ? "#ffffff"
                                  : "#f8f9fa",
                              "&:hover": {
                                bgcolor: isSelected ? "#bbdefb" : "#e3f2fd",
                                cursor: "pointer",
                              },
                            }}
                            onClick={() => handleRowSelect(row.id)}
                          >
                            {/* Número da linha */}
                            <Box
                              sx={{
                                minWidth: "50px",
                                maxWidth: "50px",
                                bgcolor: isSelected ? "#2196f3" : "#f1f3f4",
                                borderRight: "1px solid #e9ecef",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                py: 1.5,
                                px: 1,
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: isSelected ? "#ffffff" : "#6c757d",
                                  fontFamily: "monospace",
                                  fontWeight: 500,
                                }}
                              >
                                {actualRowNumber}
                              </Typography>
                            </Box>

                            {/* Células da linha */}
                            {row.cells.map((cell, cellIndex) => (
                              <Box
                                key={cellIndex}
                                sx={{
                                  minWidth: "150px",
                                  maxWidth: "300px",
                                  borderRight:
                                    cellIndex < row.cells.length - 1
                                      ? "1px solid #e9ecef"
                                      : "none",
                                  display: "flex",
                                  alignItems: "center",
                                  px: 2,
                                  py: 1.5,
                                }}
                                title={cell}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    color: "#2c3e50",
                                    lineHeight: 1.4,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    width: "100%",
                                    fontFamily:
                                      "system-ui, -apple-system, sans-serif",
                                  }}
                                >
                                  {cell || "-"}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Rodapé com paginação e informações */}
                  <Box
                    sx={{
                      bgcolor: "#f8f9fa",
                      borderTop: "2px solid #dee2e6",
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="caption" sx={{ color: "#6c757d" }}>
                        💡 Clique nos headers para ordenar • Clique nas linhas
                        para selecionar
                      </Typography>
                      {searchTerm && (
                        <Chip
                          label={`Filtrado: "${searchTerm}"`}
                          size="small"
                          onDelete={() => setSearchTerm("")}
                          color="info"
                        />
                      )}
                    </Box>

                    {totalPages > 1 && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography variant="caption" sx={{ color: "#6c757d" }}>
                          Página {currentPage} de {totalPages}
                        </Typography>
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={(_, page) => setCurrentPage(page)}
                          size="small"
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              ) : (
                /* Visualização normal para texto com números de linha */
                <Box
                  sx={{
                    display: "flex",
                    minHeight: "100%",
                    width: "100%",
                  }}
                >
                  {/* Área dos números de linha - fixa à esquerda */}
                  <Box
                    sx={{
                      minWidth: "60px",
                      bgcolor: "#f8f9fa",
                      borderRight: "1px solid #e9ecef",
                      py: 2,
                      px: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      flexShrink: 0, // Não encolhe
                    }}
                  >
                    {textContent.split("\n").map((_, index) => (
                      <Typography
                        key={index}
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "14px",
                          lineHeight: 1.5,
                          color: "#6c757d",
                          userSelect: "none",
                          minHeight: "21px", // Mesma altura da linha do conteúdo
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          pr: 1,
                        }}
                      >
                        {index + 1}
                      </Typography>
                    ))}
                  </Box>

                  {/* Área do conteúdo - flexível */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        color: "#000000 !important",
                        fontFamily: "monospace",
                        fontSize: "14px",
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                        m: 0,
                        py: 2, // Padding vertical igual ao dos números
                        px: 2, // Padding horizontal
                        border: "none",
                        borderRadius: 0,
                        boxShadow: "none",
                        background: "#ffffff !important",
                        backgroundColor: "#ffffff !important",
                        flex: 1,
                      }}
                      style={{
                        color: "#000000 !important",
                        backgroundColor: "#ffffff !important",
                        background: "#ffffff !important",
                        margin: 0,
                        paddingTop: "16px",
                        paddingBottom: "16px",
                        paddingLeft: "16px",
                        paddingRight: "16px",
                        border: "none",
                        boxShadow: "none",
                        borderRadius: 0,
                      }}
                    >
                      {textContent}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Renderização para PDFs */}
          {documentUrl &&
            !isLoadingDocument &&
            !documentError &&
            !textContent && (
              <iframe
                src={documentUrl}
                width="100%"
                height="100%"
                title={`Pré-visualização de ${attachment.name}`}
                style={{
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                }}
              />
            )}

          {/* Renderização para Imagens */}
          {imageUrl && !isLoadingDocument && !documentError && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "auto",
                p: 2,
              }}
            >
              <img
                src={imageUrl}
                alt={attachment.name}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: "4px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedDocumentPreview;
